/**
 * Cheap width/height extraction from raw SVG or HTML markup, used by the ads
 * list endpoint where running the full DOMParser-based `parseDoc` for every
 * row would be wasteful. Returns null axes when the markup doesn't carry an
 * explicit size — callers should treat null as "unknown" rather than 0.
 *
 * Handles two formats:
 *   - SVG (legacy / motion): `<svg width="W" height="H">` or `viewBox="… W H"`.
 *   - HTML (static): outermost element's `style="width:Wpx; height:Hpx"`.
 */

import { looksLikeHtml } from "./looks-like-html";

export interface Dimensions {
  width: number | null;
  height: number | null;
}

const ATTR_WIDTH = /\bwidth\s*=\s*["']?(\d+(?:\.\d+)?)/i;
const ATTR_HEIGHT = /\bheight\s*=\s*["']?(\d+(?:\.\d+)?)/i;
const VIEWBOX =
  /\bviewBox\s*=\s*["']\s*[-\d.]+\s+[-\d.]+\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)/i;
const STYLE_WIDTH_PX = /(?:^|[;{"\s])width\s*:\s*(\d+(?:\.\d+)?)px/i;
const STYLE_HEIGHT_PX = /(?:^|[;{"\s])height\s*:\s*(\d+(?:\.\d+)?)px/i;

export function extractDimensions(
  markup: string | null | undefined,
): Dimensions {
  if (!markup) return { width: null, height: null };
  return looksLikeHtml(markup)
    ? extractFromHtml(markup)
    : extractFromSvg(markup);
}

function extractFromSvg(markup: string): Dimensions {
  // Look at the first <svg ... > opening tag only, so attributes on inner
  // elements don't poison the result.
  const openTag = /<svg\b[^>]*>/i.exec(markup);
  const scope = openTag ? openTag[0] : markup;
  const w = ATTR_WIDTH.exec(scope);
  const h = ATTR_HEIGHT.exec(scope);
  if (w && h) {
    return { width: Number(w[1]), height: Number(h[1]) };
  }
  const vb = VIEWBOX.exec(scope);
  if (vb) {
    return { width: Number(vb[1]), height: Number(vb[2]) };
  }
  return {
    width: w ? Number(w[1]) : null,
    height: h ? Number(h[1]) : null,
  };
}

function extractFromHtml(markup: string): Dimensions {
  // Restrict the search to the first element's open tag — that's the canvas
  // root and its `style="width:Npx;height:Npx"` carries the canvas size.
  const openTag = /<[a-z][a-z0-9-]*\b[^>]*>/i.exec(markup);
  if (!openTag) return { width: null, height: null };
  const style = /style\s*=\s*"([^"]*)"|style\s*=\s*'([^']*)'/i.exec(openTag[0]);
  if (!style) return { width: null, height: null };
  const styleValue = style[1] ?? style[2] ?? "";
  const w = STYLE_WIDTH_PX.exec(styleValue);
  const h = STYLE_HEIGHT_PX.exec(styleValue);
  return {
    width: w ? Number(w[1]) : null,
    height: h ? Number(h[1]) : null,
  };
}
